
import apiClient from '../../../services/apiClient';

const compactParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );

export const unwrapData = (payload) => {
  if (
    payload &&
    typeof payload === 'object' &&
    Object.prototype.hasOwnProperty.call(payload, 'data')
  ) {
    return payload.data;
  }

  return payload;
};

export const unwrapList = (payload) => {
  const data = unwrapData(payload);

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

const firstMessage = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const message = firstMessage(item);
      if (message) return message;
    }
    return '';
  }

  if (typeof value === 'object') {
    const preferredKeys = [
      'detail',
      'non_field_errors',
      'amount',
      'increase_reason',
      'discount_reason',
      'amount_paid',
      'allocations',
      'reference_number',
      'fee_structure',
      'name',
      'code',
      'category',
      'academic_year',
      'class_level',
      'section',
    ];

    for (const key of preferredKeys) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        const message = firstMessage(value[key]);
        if (message) return message;
      }
    }

    for (const nestedValue of Object.values(value)) {
      const message = firstMessage(nestedValue);
      if (message) return message;
    }
  }

  return '';
};

export const getFeesApiErrorMessage = (
  error,
  fallback = 'Unable to complete the fees request.',
) => {
  const payload = error?.response?.data;
  const validationMessage = firstMessage(
    payload?.errors || payload?.data?.errors,
  );

  if (validationMessage) {
    return validationMessage;
  }

  const directMessage = firstMessage(
    payload?.detail || payload?.error || payload?.message,
  );

  if (directMessage && directMessage !== 'Request failed.') {
    return directMessage;
  }

  const status = error?.response?.status;
  if (status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  if (status === 403) {
    return 'You do not have permission to perform this fees action.';
  }
  if (status === 404) {
    return 'The requested fees record was not found.';
  }
  if (status >= 500) {
    return 'The server could not complete the fees request. Please try again.';
  }

  return error?.message || fallback;
};

export const getFeesApiFieldErrors = (error) => {
  const source = error?.response?.data?.errors;
  const output = {};

  const walk = (value, path = '') => {
    if (typeof value === 'string') {
      output[path || 'root'] = value;
      return;
    }

    if (Array.isArray(value)) {
      const message = firstMessage(value);
      if (message) output[path || 'root'] = message;
      return;
    }

    if (value && typeof value === 'object') {
      Object.entries(value).forEach(([key, nested]) => {
        const nextPath = key === 'non_field_errors'
          ? path || 'root'
          : path
            ? `${path}.${key}`
            : key;
        walk(nested, nextPath);
      });
    }
  };

  walk(source);
  return output;
};

export const feesApi = {
  getFeeConfigurationGroups: async (schoolId, params = {}) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/configuration-groups/`,
      {
        params: compactParams({
          q: params.q,
          academic_year_id:
            params.academicYearId ?? params.academic_year_id,
          status: params.status,
          page: params.page,
          page_size: params.pageSize ?? params.page_size ?? 100,
        }),
      },
    );
    return {
      items: unwrapList(response.data),
      pagination: response.data?.pagination || null,
    };
  },

  getFeeConfigurationGroup: async (schoolId, groupId) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/configuration-groups/${groupId}/`,
    );
    return unwrapData(response.data);
  },

  createFeeConfigurationGroup: async (schoolId, payload) => {
    const response = await apiClient.post(
      `/schools/${schoolId}/fees/configuration-groups/`,
      payload,
    );
    return response.data;
  },

  updateFeeConfigurationGroup: async (schoolId, groupId, payload) => {
    const response = await apiClient.patch(
      `/schools/${schoolId}/fees/configuration-groups/${groupId}/`,
      payload,
    );
    return response.data;
  },

  deleteFeeConfigurationGroup: async (schoolId, groupId) => {
    const response = await apiClient.delete(
      `/schools/${schoolId}/fees/configuration-groups/${groupId}/`,
    );
    return response.data;
  },

  syncFeeConfigurationGroup: async (schoolId, groupId, increaseReason = '') => {
    const response = await apiClient.post(
      `/schools/${schoolId}/fees/configuration-groups/${groupId}/sync/`,
      { increase_reason: increaseReason },
    );
    return response.data;
  },

  updateFeeComponent: async (schoolId, groupId, componentId, payload) => {
    const response = await apiClient.patch(
      `/schools/${schoolId}/fees/configuration-groups/${groupId}/components/${componentId}/`,
      payload,
    );
    return response.data;
  },

  getStudentOptionalFees: async (schoolId, studentId, academicYearId) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/students/${studentId}/optional-fees/`,
      { params: compactParams({ academic_year_id: academicYearId }) },
    );
    return unwrapList(response.data);
  },

  assignStudentOptionalFee: async (schoolId, studentId, payload) => {
    const response = await apiClient.post(
      `/schools/${schoolId}/fees/students/${studentId}/optional-fees/`,
      payload,
    );
    return response.data;
  },
  getFeeDashboard: async (schoolId, params = {}) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/dashboard/`,
      { params: compactParams(params) },
    );
    return unwrapData(response.data);
  },

  getFeeCategories: async (schoolId, params = {}) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/categories/`,
      { params: compactParams(params) },
    );
    return {
      items: unwrapList(response.data),
      pagination: response.data?.pagination || null,
    };
  },

  createFeeCategory: async (schoolId, payload) => {
    const response = await apiClient.post(
      `/schools/${schoolId}/fees/categories/`,
      payload,
    );
    return response.data;
  },

  updateFeeCategory: async (schoolId, categoryId, payload) => {
    const response = await apiClient.patch(
      `/schools/${schoolId}/fees/categories/${categoryId}/`,
      payload,
    );
    return response.data;
  },

  deleteFeeCategory: async (schoolId, categoryId) => {
    const response = await apiClient.delete(
      `/schools/${schoolId}/fees/categories/${categoryId}/`,
    );
    return response.data;
  },

  getFeeStructures: async (schoolId, params = {}) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/structures/`,
      { params: compactParams(params) },
    );
    return {
      items: unwrapList(response.data),
      pagination: response.data?.pagination || null,
    };
  },

  createFeeStructure: async (schoolId, payload) => {
    const response = await apiClient.post(
      `/schools/${schoolId}/fees/structures/`,
      payload,
    );
    return response.data;
  },

  syncFeeStructure: async (schoolId, structureId, increaseReason = '') => {
    const response = await apiClient.post(
      `/schools/${schoolId}/fees/structures/${structureId}/sync/`,
      { increase_reason: increaseReason },
    );
    return response.data;
  },

  createFeeHead: async (schoolId, structureId, payload) => {
    const response = await apiClient.post(
      `/schools/${schoolId}/fees/structures/${structureId}/heads/`,
      payload,
    );
    return response.data;
  },

  getFeeHead: async (schoolId, structureId, headId) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/structures/${structureId}/heads/${headId}/`,
    );
    return unwrapData(response.data);
  },

  previewFeeHeadIncrease: async (schoolId, structureId, headId, amount) => {
    const response = await apiClient.post(
      `/schools/${schoolId}/fees/structures/${structureId}/heads/${headId}/increase-preview/`,
      { amount },
    );
    return unwrapData(response.data);
  },

  updateFeeHead: async (schoolId, structureId, headId, payload) => {
    const response = await apiClient.patch(
      `/schools/${schoolId}/fees/structures/${structureId}/heads/${headId}/`,
      payload,
    );
    return response.data;
  },

  deleteFeeHead: async (schoolId, structureId, headId) => {
    const response = await apiClient.delete(
      `/schools/${schoolId}/fees/structures/${structureId}/heads/${headId}/`,
    );
    return response.data;
  },

  searchStudents: async (schoolId, params = {}) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/students/search/`,
      {
        params: compactParams({
          q: params.q,
          academic_year_id: params.academicYearId,
          class_level_id: params.classLevelId,
          section_id: params.sectionId,
          pending_only: params.pendingOnly,
          overdue: params.overdue,
          page: params.page || 1,
          page_size: params.pageSize || 20,
        }),
      },
    );

    return {
      items: unwrapList(response.data),
      pagination: response.data?.pagination || null,
    };
  },

  getStudentLedger: async (schoolId, studentId, params = {}) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/students/${studentId}/ledger/`,
      {
        params: compactParams({
          academic_year_id: params.academicYearId,
          status: params.status,
          overdue: params.overdue,
          pending_only: params.pendingOnly,
          all: params.all ?? true,
        }),
      },
    );
    return unwrapList(response.data);
  },

  getStudentPaymentHistory: async (schoolId, studentId, params = {}) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/students/${studentId}/payments/`,
      {
        params: compactParams({
          academic_year_id: params.academicYearId,
          date_from: params.dateFrom,
          date_to: params.dateTo,
          payment_mode: params.paymentMode,
          status: params.status,
        }),
      },
    );
    return unwrapList(response.data);
  },

  collectFee: async (schoolId, payload) => {
    const response = await apiClient.post(
      `/schools/${schoolId}/fees/collect/`,
      payload,
    );
    return response.data;
  },

  getReceipt: async (schoolId, receiptId) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/receipts/${receiptId}/`,
    );
    return unwrapData(response.data);
  },

  getReceiptPdfBlob: async (schoolId, receiptId) => {
    const response = await apiClient.get(
      `/schools/${schoolId}/fees/receipts/${receiptId}/pdf/`,
      { responseType: 'blob' },
    );
    return response.data;
  },
};

export default feesApi;
